/**
 * Utility functions for localStorage management with quota handling
 */

/**
 * Get the size of a string in bytes
 */
const getStringSize = (str: string): number => {
    return new Blob([str]).size;
};

/**
 * Get the total size of localStorage in bytes
 */
export const getLocalStorageSize = (): number => {
    let total = 0;
    for (const key in localStorage) {
        if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
            total += getStringSize(localStorage[key] + key);
        }
    }
    return total;
};

/**
 * Get the size of a specific localStorage item
 */
export const getItemSize = (key: string): number => {
    const item = localStorage.getItem(key);
    if (!item) return 0;
    return getStringSize(item + key);
};

/**
 * Clear old cache entries based on timestamp
 * Keeps the most recent entries and removes older ones
 */
export const clearOldCacheEntries = (prefix: string, maxAge: number = 7 * 24 * 60 * 60 * 1000): void => {
    const now = Date.now();
    const keysToRemove: string[] = [];

    Object.keys(localStorage).forEach(key => {
        if (key.startsWith(prefix)) {
            try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.timestamp && (now - data.timestamp) > maxAge) {
                    keysToRemove.push(key);
                }
            } catch (e) {
                // If parsing fails, consider it old and remove
                keysToRemove.push(key);
            }
        }
    });

    keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Clear cache entries for a specific prefix, keeping only the most recent N entries
 */
export const clearOldCacheEntriesByCount = (prefix: string, keepCount: number = 3): void => {
    const entries: Array<{ key: string; timestamp: number }> = [];

    Object.keys(localStorage).forEach(key => {
        if (key.startsWith(prefix)) {
            try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                entries.push({
                    key,
                    timestamp: data.timestamp || 0
                });
            } catch (e) {
                // If parsing fails, add with timestamp 0 (oldest)
                entries.push({ key, timestamp: 0 });
            }
        }
    });

    // Sort by timestamp (newest first)
    entries.sort((a, b) => b.timestamp - a.timestamp);

    // Remove entries beyond keepCount
    entries.slice(keepCount).forEach(entry => {
        localStorage.removeItem(entry.key);
    });
};

/**
 * Safe setItem that handles quota exceeded errors
 * Returns true if successful, false if quota exceeded
 */
export const safeSetItem = (key: string, value: string): boolean => {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (e: any) {
        if (e.name === 'QuotaExceededError' || e.code === 22) {
            console.warn(`localStorage quota exceeded for key: ${key}`);
            
            // Try to clear old cache entries
            const prefixes = [
                'tv_package_channels_cache',
                'preference_channels_cache',
                'tv_packages_cache',
                'tv_addons_cache',
                'lineups_cache',
                'screenGroups-'
            ];

            prefixes.forEach(prefix => {
                clearOldCacheEntriesByCount(prefix, 2); // Keep only 2 most recent
            });

            // Try again after cleanup
            try {
                localStorage.setItem(key, value);
                return true;
            } catch (retryError) {
                console.error('Still quota exceeded after cleanup. Consider reducing cache size or using IndexedDB.');
                return false;
            }
        }
        throw e;
    }
};

/**
 * Clear all GameMaster related cache
 * Includes localStorage cache and optionally React Query cache
 */
export const clearGameMasterCache = (workplaceId?: number, queryClient?: any): void => {
    const prefixes = [
        'tv_package_channels_cache',
        'preference_channels_cache',
        'tv_packages_cache',
        'tv_addons_cache',
        'lineups_cache',
        'gracenote_lineups_cache',
    ];

    // Additional cache keys that don't use the prefix pattern
    const additionalKeys = [
        'gracenoteDefaults',
    ];

    if (workplaceId) {
        // Clear specific workplace cache
        prefixes.forEach(prefix => {
            localStorage.removeItem(`${prefix}_${workplaceId}`);
        });
        localStorage.removeItem(`screenGroups-${workplaceId}`);
        
        // Clear additional keys if they're workplace-specific
        // Note: gracenoteDefaults is global, so we clear it when switching workplaces
        additionalKeys.forEach(key => {
            localStorage.removeItem(key);
        });
    } else {
        // Clear all cache entries
        Object.keys(localStorage).forEach(key => {
            if (prefixes.some(prefix => key.startsWith(prefix)) || 
                key.startsWith('screenGroups-') ||
                additionalKeys.includes(key)) {
                localStorage.removeItem(key);
            }
        });
    }

    // Clear React Query cache if queryClient is provided
    if (queryClient) {
        try {
            // Invalidate all GameMaster-related queries
            const queryKeys = [
                ['gameMaster'],
                ['screenGroups'],
                ['sportsPreferences'],
                ['gracenoteLineups'],
                ['gracenoteDefaults'],
                ['tvPackages'],
                ['tvChannels'],
                ['preferenceChannels'],
            ];

            queryKeys.forEach(queryKey => {
                if (workplaceId) {
                    // Invalidate queries for specific workplace
                    queryClient.invalidateQueries([...queryKey, workplaceId]);
                    queryClient.removeQueries([...queryKey, workplaceId]);
                } else {
                    // Invalidate all queries matching the pattern
                    queryClient.invalidateQueries(queryKey);
                    queryClient.removeQueries(queryKey);
                }
            });
        } catch (error) {
            console.warn('Failed to clear React Query cache:', error);
        }
    }
};

